import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, Clock, Video, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

const bookingSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  company: z.string().min(2, "Company name is required"),
  phone: z.string().optional(),
  preferredDate: z.string().min(1, "Please select a date"),
  preferredTime: z.string().min(1, "Please select a time"),
  attendees: z.string().optional(),
});

type BookingForm = z.infer<typeof bookingSchema>;

const DemoBooking = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBooked, setIsBooked] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<BookingForm>({
    resolver: zodResolver(bookingSchema),
  });

  const timeSlots = [
    "09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM"
  ];

  const benefits = [
    "See our AI voice platform in action",
    "Discuss your specific use cases",
    "Get ROI estimates for your business",
    "Q&A with our solution experts"
  ];

  const onSubmit = async (data: BookingForm) => {
    setIsSubmitting(true);

    try {
      // In production, this would integrate with Calendly, Cal.com, or your booking system
      // For now, we'll simulate the booking
      await new Promise(resolve => setTimeout(resolve, 1500));

      console.log("Demo booking:", data);

      toast({
        title: "Demo Booked Successfully! 🎉",
        description: "Check your email for calendar invite and meeting details.",
      });

      setIsBooked(true);
      reset();
    } catch (error) {
      toast({
        title: "Booking Failed",
        description: "Something went wrong. Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isBooked) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <Card className="card-dark gradient-border p-12 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
          >
            <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-6" />
          </motion.div>
          <h3 className="text-3xl font-bold mb-4">Demo Booked Successfully!</h3>
          <p className="text-foreground/70 mb-6 max-w-md mx-auto">
            We've sent you a calendar invite with the meeting details. Our team will reach out before the demo to understand your specific needs.
          </p>
          <Button 
            onClick={() => setIsBooked(false)}
            variant="outline"
            className="border-accent text-accent hover:bg-accent/10"
          >
            Book Another Demo
          </Button>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <Card className="card-dark gradient-border p-8">
        <div className="flex items-center gap-3 mb-6">
          <Video className="w-6 h-6 text-accent" />
          <h3 className="text-2xl font-bold">Schedule Your Demo</h3>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              {...register("name")}
              placeholder="John Doe"
              className="mt-1"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="email">Work Email *</Label>
            <Input
              id="email"
              type="email"
              {...register("email")}
              placeholder="john@company.com"
              className="mt-1"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="company">Company Name *</Label>
            <Input
              id="company"
              {...register("company")}
              placeholder="Acme Corp"
              className="mt-1"
            />
            {errors.company && (
              <p className="text-red-500 text-sm mt-1">{errors.company.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              {...register("phone")}
              placeholder="+49 123 456789"
              className="mt-1"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="preferredDate" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Preferred Date *
              </Label>
              <Input
                id="preferredDate"
                type="date"
                {...register("preferredDate")}
                min={new Date().toISOString().split('T')[0]}
                className="mt-1"
              />
              {errors.preferredDate && (
                <p className="text-red-500 text-sm mt-1">{errors.preferredDate.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="preferredTime" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Preferred Time *
              </Label>
              <select
                id="preferredTime"
                {...register("preferredTime")}
                className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-accent focus:ring-2 focus:ring-accent/20"
              >
                <option value="">Select time</option>
                {timeSlots.map(slot => (
                  <option key={slot} value={slot}>{slot}</option>
                ))}
              </select>
              {errors.preferredTime && (
                <p className="text-red-500 text-sm mt-1">{errors.preferredTime.message}</p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="attendees">Additional Attendees (Optional)</Label>
            <Input
              id="attendees"
              {...register("attendees")}
              placeholder="colleague@company.com, manager@company.com"
              className="mt-1"
            />
            <p className="text-xs text-foreground/60 mt-1">
              Separate multiple emails with commas
            </p>
          </div>

          <Button 
            type="submit" 
            className="w-full gradient-brand text-white hover:opacity-90 h-12 text-base"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Booking..." : "Schedule Demo"}
          </Button>
        </form>

        <p className="text-xs text-foreground/60 text-center mt-4">
          By booking a demo, you agree to our terms and privacy policy
        </p>
      </Card>

      <div className="space-y-6">
        <Card className="card-dark gradient-border p-8 bg-gradient-to-br from-gradient-pink/10 to-gradient-purple/10">
          <h3 className="text-xl font-bold mb-4">What to Expect</h3>
          <ul className="space-y-3">
            {benefits.map((benefit, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3"
              >
                <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <span className="text-foreground/80">{benefit}</span>
              </motion.li>
            ))}
          </ul>
        </Card>

        <Card className="card-dark gradient-border p-8">
          <h3 className="text-xl font-bold mb-4">Demo Format</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Duration</p>
                <p className="text-sm text-foreground/70">45-60 minutes</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Video className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Format</p>
                <p className="text-sm text-foreground/70">Video call via Teams/Zoom</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Availability</p>
                <p className="text-sm text-foreground/70">Monday-Friday, 9 AM - 5 PM CET</p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="card-dark gradient-border p-6 text-center glow-effect">
          <p className="text-sm text-foreground/70 mb-2">Need immediate assistance?</p>
          <Button variant="outline" className="border-accent text-accent hover:bg-accent/10">
            Contact Sales Now
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default DemoBooking;
